import React, {useMemo} from 'react';
import clsx from 'clsx';
import {FormLabel} from '@mui/material';
import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import {ChipSkeleton} from '@netx/core-components/Chip';
import {isEmptyValue} from '@netx/core-components/Form';
import type {Theme} from '@netx/core-components/Theme';
import {i18n} from '@netx/core/i18n';
import type {VirtualizedInputProps} from '../SelectFieldInput';
import ValueChip from '../ValueChip';

type Value =
	| string
	| {
			[any_other_key: string]: any;
			label: string;
	  };

const getLabels = (values?: Value[]): string[] => {
	if (!values) return [];
	if (!Array.isArray(values)) values = [values];
	// @ts-ignore
	return values.map((value) => (value.label != null ? value.label : value));
};

const readOnlyChipStyles = (theme: Theme) =>
	createStyles({
		root: {
			disaply: 'flex',
			margin: 3,
			gap: 3,
		},
	});

const ReadOnlyChips = withStyles(readOnlyChipStyles)(
	React.memo(
		({
			classes,
			onSearch,
			values,
		}: WithStyles<typeof readOnlyChipStyles> & {
			onSearch?: (value: string) => void;
			values?: Value[];
		}) => {
			return (
				<>
					{getLabels(values).map((label, index) => (
						<ValueChip
							key={label}
							className={classes.root}
							onSearch={onSearch}
							value={label}
						/>
					))}
				</>
			);
		},
	),
);

const styles = ({mixins, palette, spacing}: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flexDirection: 'column',
			margin: spacing(0.75, 0),
			padding: spacing(0, 1.75),
			width: '100%',
			'&.disabled': {
				...mixins.disabled,
			},
			'&.pseudoValue': {
				'& $value': {
					color: palette.text.secondary,
				},
			},
			'&$limitHeightToContainer': {
				maxHeight: '100%',
				display: 'flex',
				flexDirection: 'column',
				'& $label': {
					flex: '0 0 auto',
				},
				'& $value': {
					...mixins.scrollable,
				},
			},
		},
		label: {
			...mixins.readOnlyLabel,
		},
		limitHeightToContainer: {},
		value: {
			display: 'flex',
			flexWrap: 'wrap',
			marginTop: spacing(0.5),
		},
	});

export interface ReadOnlyMultiSelectFieldProps extends Omit<
	VirtualizedInputProps,
	'name' | 'onChange'
> {
	limitHeightToContainer?: boolean;
}

const ReadOnlyMultiSelectField = ({
	classes,
	disabled,
	label,
	limitHeightToContainer,
	loading,
	loadingValue,
	multipleValues,
	onSearch,
	value,
}: WithStyles<typeof styles> & ReadOnlyMultiSelectFieldProps) => {
	value = value == null ? [] : value;
	const pseudoValue = isEmptyValue(value) || multipleValues;

	const content = useMemo(() => {
		if (loading || loadingValue) return <ChipSkeleton count={3} />;
		if (multipleValues) return i18n.t('i18n.multipleValues');
		if (!value?.length) return '(none)';
		return <ReadOnlyChips values={value} onSearch={onSearch} />;
	}, [loading, loadingValue, multipleValues, onSearch, value]);

	const displayLabel = useMemo(() => {
		if (label == null || label === '') return null;
		return (
			<FormLabel
				className={classes.label}
				title={typeof label === 'string' ? label : undefined}
			>
				{label}
			</FormLabel>
		);
	}, [classes, label]);

	return (
		<div
			className={clsx(classes.root, 'read-only-multiselect', {
				[classes.limitHeightToContainer]: limitHeightToContainer,
				pseudoValue: pseudoValue,
				disabled: disabled,
			})}
		>
			{displayLabel}
			<div className={classes.value}>{content}</div>
		</div>
	);
};

export default withStyles(styles, {
	name: 'ReadOnlyMultiSelect',
})(React.memo(ReadOnlyMultiSelectField));
